import React from 'react';
import { Tractor, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { Spinner } from '@components/spinner';
import { Button } from '@components/ui/button';
import { Card } from '@components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { PageHeader } from '../components/page-header';
import {
    BadgeTree,
    BadgeTreeNode,
    BadgeNodeType,
    BadgeTreeHandle
} from './badge-tree';
import { BadgeDetailPanel } from './badge-detail-panel';
import {
    BadgeCategoryAdminDto,
    BadgeTypeAdminDto,
    BadgeLevelAdminDto
} from '@api/FitnessActivityClient';
import { Api } from '../core/api';
import { toast } from '../core/toast';
import { injectTSDI } from '../core/tsdi';
import { getLocalizedName } from './badge-utils';

export const Badges = observer(() => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const treeRef = React.useRef<BadgeTreeHandle>(null);
    const [selectedNode, setSelectedNode] =
        React.useState<BadgeTreeNode | null>(null);
    const [createMode, setCreateMode] = React.useState<{
        nodeType: BadgeNodeType;
        parentId?: number;
    } | null>(null);
    const [isSeeding, setIsSeeding] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleDeleteAllBadges = async () => {
        if (!window.confirm('Delete ALL badges? This cannot be undone.'))
            return;
        setIsDeleting(true);
        try {
            await fitnessActivityClient.admin.badgeSeedSupportControllerDeleteAllBadges();
            toast.success('All badges deleted');
            window.location.reload();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to delete');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSeedBadges = async () => {
        if (!window.confirm('Seed default badge data?')) return;
        setIsSeeding(true);
        try {
            await fitnessActivityClient.admin.badgeSeedSupportControllerTriggerBadgeSeed();
            toast.success('Seed completed');
            window.location.reload();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to seed');
        } finally {
            setIsSeeding(false);
        }
    };

    const handleSelect = (node: BadgeTreeNode | null) => {
        setSelectedNode(node);
        setCreateMode(null);
    };

    const handleCreateRequest = (
        nodeType: BadgeNodeType,
        parentId?: number
    ) => {
        setCreateMode({ nodeType, parentId });
        setSelectedNode(null);
    };

    const handleNodeUpdated = (
        nodeId: string,
        data: BadgeCategoryAdminDto | BadgeTypeAdminDto | BadgeLevelAdminDto
    ) => {
        treeRef.current?.updateNode(nodeId, data);
        if (selectedNode && selectedNode.id === nodeId) {
            const name = data.name as Record<string, string>;
            const label =
                'levelIndex' in data
                    ? `${(data as BadgeLevelAdminDto).levelIndex}. ${getLocalizedName(name)}`
                    : getLocalizedName(name);
            setSelectedNode({ ...selectedNode, label, data });
        }
    };

    const handleNodeCreated = (
        nodeType: BadgeNodeType,
        data: BadgeCategoryAdminDto | BadgeTypeAdminDto | BadgeLevelAdminDto,
        parentId?: number
    ) => {
        const name = data.name as Record<string, string>;
        const label =
            'levelIndex' in data
                ? `${(data as BadgeLevelAdminDto).levelIndex}. ${getLocalizedName(name)}`
                : getLocalizedName(name);

        const newNode: BadgeTreeNode = {
            id: `${nodeType}-${data.id}`,
            label,
            nodeType,
            entityId: data.id,
            parentId,
            data,
            children: nodeType === 'level' ? undefined : []
        };

        let parentNodeId: string | undefined;
        if (nodeType === 'type' && parentId)
            parentNodeId = `category-${parentId}`;
        else if (nodeType === 'level' && parentId)
            parentNodeId = `type-${parentId}`;

        treeRef.current?.addNode(newNode, parentNodeId);
        setCreateMode(null);
        setSelectedNode(newNode);
    };

    const handleNodeDeleted = (nodeId: string) => {
        treeRef.current?.removeNode(nodeId);
        if (selectedNode?.id === nodeId) setSelectedNode(null);
    };

    const handleCancel = () => setCreateMode(null);

    return (
        <div className="flex h-[calc(100vh-9rem)] flex-col">
            <PageHeader
                title="Badges"
                actions={
                    <>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={handleSeedBadges}
                                    disabled={isSeeding || isDeleting}
                                >
                                    {isSeeding ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <Tractor />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Seed default badges</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    className="text-destructive"
                                    onClick={handleDeleteAllBadges}
                                    disabled={isSeeding || isDeleting}
                                >
                                    {isDeleting ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <Trash2 />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete all badges</TooltipContent>
                        </Tooltip>
                    </>
                }
            />

            <Card className="flex min-h-0 flex-1 flex-row gap-0 overflow-hidden py-0">
                <div className="flex w-72 min-w-60 shrink-0 flex-col border-r">
                    <BadgeTree
                        ref={treeRef}
                        onSelect={handleSelect}
                        onCreateRequest={handleCreateRequest}
                        selectedNodeId={selectedNode?.id}
                    />
                </div>

                <div className="min-w-0 flex-1 overflow-auto">
                    <BadgeDetailPanel
                        selectedNode={selectedNode}
                        createMode={createMode}
                        onNodeUpdated={handleNodeUpdated}
                        onNodeCreated={handleNodeCreated}
                        onNodeDeleted={handleNodeDeleted}
                        onCancel={handleCancel}
                    />
                </div>
            </Card>
        </div>
    );
});

export default Badges;
